import React, { useState, useEffect, useRef } from 'react';

const SchoolFilter = ({ 
  currentYear, 
  yearData, 
  selectedSchoolNames,
  onSchoolsFilter,
  onClearFilter,
  sidebar = false,
  // NEW: Color coding props
  colorBySchool = false,
  onToggleColorMode,
  schoolColorMap = new Map(),
  schoolColors = [],
  selectedSchools = []
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [internalSelectedSchools, setInternalSelectedSchools] = useState([]);
  const [allSchools, setAllSchools] = useState([]);
  const [filteredSchools, setFilteredSchools] = useState([]);
  const [showAllSelected, setShowAllSelected] = useState(false);
  const dropdownRef = useRef(null);

  // Use external selectedSchools if provided, otherwise use internal state
  const currentSelectedSchools = selectedSchools.length > 0 ? selectedSchools : internalSelectedSchools;

  // ENHANCED: Smart school name normalization for cross-year consistency
  const normalizeSchoolName = (name, type) => {
    if (!name || typeof name !== 'string') return '';
    
    // Remove common suffixes and clean up the name
    let normalized = name.trim()
      .replace(/\s+/g, ' ') // Normalize whitespace
      .replace(/\s*(Elementary|ES|E\.S\.)(\s*School)?$/i, '') // Remove elementary suffixes
      .replace(/\s*(Middle|MS|M\.S\.)(\s*School)?$/i, '') // Remove middle school suffixes
      .replace(/\s*(High|HS|H\.S\.)(\s*School)?$/i, '') // Remove high school suffixes
      .replace(/\s*School$/i, '') // Remove trailing "School"
      .trim();

    // Handle special cases for Montgomery County schools
    const specialCases = {
      'B-CC': 'Bethesda-Chevy Chase',
      'BCC': 'Bethesda-Chevy Chase',
      'Bethesda Chevy Chase': 'Bethesda-Chevy Chase',
      'RMS': 'Richard Montgomery',
      'RM': 'Richard Montgomery',
      'WJ': 'Walter Johnson',
      'Blair': 'Montgomery Blair',
      'Einstein': 'Albert Einstein',
      'Wootton': 'Thomas S. Wootton',
      'Churchill': 'Winston Churchill',
      'Whitman': 'Walt Whitman'
    };

    if (specialCases[normalized]) {
      normalized = specialCases[normalized];
    }

    return normalized;
  };

  // ENHANCED: Create standardized school identifier that works across years
  const createSchoolKey = (name, type) => {
    const normalized = normalizeSchoolName(name, type);
    return `${type}:${normalized}`;
  };

  // ENHANCED: Smart school name extraction that handles 2024 data structure
  const extractSchoolName = (feature, index, type) => {
    const props = feature.properties || {};
    
    // 2024-2025 specific field extraction
    if (currentYear === '2024-2025') {
      if (type === 'elementary') {
        return props.ES_csv_S_N || props.ES_csv_P_N || props.S_NAME || props.NAME || `Elementary School ${index + 1}`;
      } else if (type === 'middleSchool') {
        return props.MS_csv_S_N || props.MS_csv_P_N || props.S_NAME || props.NAME || `Middle School ${index + 1}`;
      } else if (type === 'highSchool') {
        return props.HS_csv_S_N || props.HS_csv_C_N || props.HS_csv_P_N || props.CLUSTER || props.C_NAME || props.S_NAME || props.NAME || `High School ${index + 1}`;
      }
    }
    
    // Standard extraction for other years
    if (type === 'elementary' || type === 'middleSchool') {
      return props.S_NAME || 
             props.SCHNAME || 
             props.NAME ||
             props.SCHOOLNAME ||
             props.School_Name ||
             props.school_name ||
             `${type === 'elementary' ? 'Elementary' : 'Middle'} School ${index + 1}`;
    } else { // highSchool
      return props.CLUSTER || 
             props.C_NAME || 
             props.S_NAME || 
             props.NAME ||
             props.SCHOOLNAME ||
             props.School_Name ||
             props.school_name ||
             props.SCHNAME ||
             `High School ${index + 1}`;
    }
  };

  // Extract all schools from current year data with enhanced normalization
  useEffect(() => {
    if (!yearData[currentYear]) {
      setAllSchools([]);
      setFilteredSchools([]);
      return;
    }

    const data = yearData[currentYear];
    const schoolsMap = new Map();

    // Process elementary schools
    if (data.elementary?.features) {
      data.elementary.features.forEach((feature, index) => {
        const rawName = extractSchoolName(feature, index, 'elementary');
        
        const normalizedName = normalizeSchoolName(rawName, 'elementary');
        const schoolKey = createSchoolKey(rawName, 'elementary');
        
        if (!schoolsMap.has(schoolKey)) {
          schoolsMap.set(schoolKey, {
            id: `elementary-${normalizedName.replace(/\s+/g, '-').toLowerCase()}`,
            name: rawName, // Keep original name for display
            normalizedName: normalizedName,
            type: 'elementary',
            feature: feature,
            cluster: feature.properties?.CLUSTER || feature.properties?.C_NAME || 'N/A',
            schoolKey: schoolKey
          });
        }
      });
    }

    // Process middle schools
    if (data.middleSchool?.features) {
      data.middleSchool.features.forEach((feature, index) => {
        const rawName = extractSchoolName(feature, index, 'middleSchool');
        
        const normalizedName = normalizeSchoolName(rawName, 'middleSchool');
        const schoolKey = createSchoolKey(rawName, 'middleSchool');
        
        if (!schoolsMap.has(schoolKey)) {
          schoolsMap.set(schoolKey, {
            id: `middle-${normalizedName.replace(/\s+/g, '-').toLowerCase()}`,
            name: rawName,
            normalizedName: normalizedName,
            type: 'middleSchool',
            feature: feature,
            cluster: feature.properties?.CLUSTER || feature.properties?.C_NAME || 'N/A',
            schoolKey: schoolKey
          });
        }
      });
    }

    // Process high schools with enhanced 1999 support
    if (data.highSchool?.features) {
      data.highSchool.features.forEach((feature, index) => {
        const rawName = extractSchoolName(feature, index, 'highSchool');
        
        const normalizedName = normalizeSchoolName(rawName, 'highSchool');
        const schoolKey = createSchoolKey(rawName, 'highSchool');
        
        if (!schoolsMap.has(schoolKey)) {
          schoolsMap.set(schoolKey, {
            id: `high-${normalizedName.replace(/\s+/g, '-').toLowerCase()}`,
            name: rawName,
            normalizedName: normalizedName,
            type: 'highSchool',
            feature: feature,
            cluster: feature.properties?.CLUSTER || feature.properties?.C_NAME || rawName,
            schoolKey: schoolKey
          });
        }
      });
    }

    const schools = Array.from(schoolsMap.values());
    schools.sort((a, b) => a.normalizedName.localeCompare(b.normalizedName));
    
    setAllSchools(schools);
    setFilteredSchools(schools);
  }, [currentYear, yearData]);

  // ENHANCED: Smart selection persistence with name matching
  useEffect(() => {
    if (allSchools.length === 0) return;

    const persistentSelections = [];
    
    if (selectedSchoolNames) {
      selectedSchoolNames.forEach(selectedName => {
        // Direct match first
        const directMatch = allSchools.find(school => school.schoolKey === selectedName);
        if (directMatch) {
          persistentSelections.push(directMatch);
          return;
        }
        
        // Smart matching by normalized name
        const [type, searchName] = selectedName.split(':');
        const searchNormalized = normalizeSchoolName(searchName, type);
        
        const smartMatch = allSchools.find(school => 
          school.type === type && 
          normalizeSchoolName(school.name, school.type) === searchNormalized
        );
        
        if (smartMatch) {
          persistentSelections.push(smartMatch);
        }
      });
    }
    
    setInternalSelectedSchools(persistentSelections);
  }, [allSchools, selectedSchoolNames]);

  // Filter schools based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredSchools(allSchools);
      return;
    }

    const filtered = allSchools.filter(school => 
      school.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      school.normalizedName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      school.cluster.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    setFilteredSchools(filtered);
  }, [searchTerm, allSchools]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSchoolToggle = (school) => {
    const isCurrentlySelected = currentSelectedSchools.some(s => s.id === school.id);
    
    const newSelected = isCurrentlySelected
      ? currentSelectedSchools.filter(s => s.id !== school.id)
      : [...currentSelectedSchools, school];
    
    setInternalSelectedSchools(newSelected);
    
    if (onSchoolsFilter) {
      onSchoolsFilter(newSelected);
    }
  };

  const handleRemoveSchool = (schoolToRemove, event) => {
    if (event) {
      event.stopPropagation();
    }

    const newSelected = currentSelectedSchools.filter(s => s.id !== schoolToRemove.id);
    setInternalSelectedSchools(newSelected);
    
    if (onSchoolsFilter) {
      onSchoolsFilter(newSelected);
    }
  };

  const handleClearAll = () => {
    setInternalSelectedSchools([]);
    setSearchTerm('');
    setShowAllSelected(false);
    if (onClearFilter) {
      onClearFilter();
    }
  };

  const toggleShowAllSelected = () => {
    setShowAllSelected(!showAllSelected);
  };

  const getSchoolTypeColor = (type) => {
    switch (type) {
      case 'elementary': return '#3B82F6';
      case 'middleSchool': return '#10B981';
      case 'highSchool': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const getSchoolTypeLabel = (type) => {
    switch (type) {
      case 'elementary': return 'Elementary';
      case 'middleSchool': return 'Middle';
      case 'highSchool': return 'High';
      default: return 'School';
    }
  };

  // NEW: Get school's assigned color from color map
  const getSchoolAssignedColor = (school) => {
    if (!colorBySchool || !schoolColorMap) return null;
    
    const normalizedName = normalizeSchoolName(school.name, school.type);
    const normalizedKey = `${school.type}:${normalizedName}`;
    
    let assignedColor = schoolColorMap.get(normalizedKey);
    if (!assignedColor && school.schoolKey) {
      assignedColor = schoolColorMap.get(school.schoolKey);
    }
    
    return assignedColor;
  };

  const containerStyle = sidebar ? {
    background: 'transparent',
    backdropFilter: 'none',
    borderRadius: '0',
    padding: '0',
    border: 'none',
    boxShadow: 'none',
    width: '100%',
    minWidth: 'auto',
    maxWidth: 'none',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Inter", sans-serif'
  } : {
    background: 'rgba(15, 20, 25, 0.9)',
    backdropFilter: 'blur(20px)',
    borderRadius: '16px',
    padding: '20px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.4)',
    minWidth: '320px',
    maxWidth: '400px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Inter", sans-serif'
  };

  return (
    <div ref={dropdownRef} style={containerStyle}>
      {/* Header - only show in standalone mode */}
      {!sidebar && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '16px'
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)'
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
              <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"/>
            </svg>
          </div>
          <div>
            <h3 style={{ 
              margin: '0', 
              fontSize: '16px', 
              fontWeight: '600',
              color: 'rgba(255, 255, 255, 0.95)',
              letterSpacing: '-0.01em'
            }}>
              School Filter
            </h3>
            <p style={{
              margin: '2px 0 0 0',
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.5)',
              fontWeight: '400'
            }}>
              Search and select schools ({currentYear})
            </p>
          </div>
        </div>
      )}

      {/* NEW: Color Mode Toggle - Show when schools are selected and callback is provided */}
      {currentSelectedSchools.length > 0 && currentSelectedSchools.length <= 12 && onToggleColorMode && (
        <div style={{
          background: 'rgba(139, 92, 246, 0.1)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '16px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div>
              <div style={{
                fontSize: '13px',
                fontWeight: '600',
                color: 'rgba(139, 92, 246, 0.9)',
                marginBottom: '2px'
              }}>
                Color Coding
              </div>
              <div style={{
                fontSize: '11px',
                color: 'rgba(255, 255, 255, 0.6)',
                lineHeight: '1.3'
              }}>
                {colorBySchool ? 'Each school has unique colors' : 'Schools grouped by type'}
              </div>
            </div>
            <button
              onClick={onToggleColorMode}
              style={{
                background: colorBySchool ? 
                  'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)' : 
                  'rgba(255, 255, 255, 0.1)',
                border: '1px solid ' + (colorBySchool ? '#8B5CF6' : 'rgba(255, 255, 255, 0.2)'),
                borderRadius: '6px',
                padding: '8px 12px',
                color: colorBySchool ? 'white' : 'rgba(255, 255, 255, 0.7)',
                fontSize: '11px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              onMouseEnter={(e) => {
                if (!colorBySchool) {
                  e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.target.style.color = 'rgba(255, 255, 255, 0.9)';
                }
              }}
              onMouseLeave={(e) => {
                if (!colorBySchool) {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.color = 'rgba(255, 255, 255, 0.7)';
                }
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12,2C13.1,2 14,2.9 14,4C14,5.1 13.1,6 12,6C10.9,6 10,5.1 10,4C10,2.9 10.9,2 12,2ZM21,9V7L15,1H5C3.89,1 3,1.89 3,3V21A3,3 0 0,0 6,24H18A3,3 0 0,0 21,21V9M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8Z"/>
              </svg>
              {colorBySchool ? 'Individual' : 'By Type'}
            </button>
          </div>

          {/* NEW: Color Preview - Show assigned colors when color mode is active */}
          {colorBySchool && currentSelectedSchools.length > 0 && (
            <div style={{
              marginTop: '12px',
              paddingTop: '12px',
              borderTop: '1px solid rgba(139, 92, 246, 0.2)'
            }}>
              <div style={{
                fontSize: '11px',
                color: 'rgba(139, 92, 246, 0.8)',
                marginBottom: '8px',
                fontWeight: '500'
              }}>
                Assigned Colors:
              </div>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px'
              }}>
                {currentSelectedSchools.slice(0, 6).map((school, index) => {
                  const assignedColor = getSchoolAssignedColor(school);
                  return (
                    <div key={school.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '4px',
                      padding: '3px 6px'
                    }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: assignedColor || getSchoolTypeColor(school.type),
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}></div>
                      <span style={{
                        fontSize: '9px',
                        color: 'rgba(255, 255, 255, 0.7)',
                        maxWidth: '60px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {school.name}
                      </span>
                    </div>
                  );
                })}
                {currentSelectedSchools.length > 6 && (
                  <div style={{
                    fontSize: '9px',
                    color: 'rgba(255, 255, 255, 0.5)',
                    padding: '3px 6px',
                    alignSelf: 'center'
                  }}>
                    +{currentSelectedSchools.length - 6} more
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Search Input */}
      <div style={{ position: 'relative', marginBottom: '16px' }}>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={(e) => {
            setIsOpen(true);
            e.target.style.borderColor = 'rgba(16, 185, 129, 0.5)';
            e.target.style.background = 'rgba(255, 255, 255, 0.12)';
          }}
          placeholder="Search schools by name or cluster..."
          style={{
            width: '100%',
            padding: '10px 40px 10px 12px',
            background: 'rgba(255, 255, 255, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '8px',
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '13px',
            fontFamily: 'inherit',
            outline: 'none',
            transition: 'all 0.2s ease',
            boxSizing: 'border-box'
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'rgba(255, 255, 255, 0.12)';
            e.target.style.background = 'rgba(255, 255, 255, 0.08)';
          }}
        />
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            color: 'rgba(255, 255, 255, 0.6)',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '4px',
            transition: 'color 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.9)'}
          onMouseLeave={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.6)'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>
            <path d="M7 10l5 5 5-5z"/>
          </svg>
        </button>
      </div>

      {/* Selected Schools Summary - ALWAYS RENDERED when schools are selected */}
      {currentSelectedSchools.length > 0 && (
        <div style={{
          background: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '16px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <span style={{
              fontSize: '12px',
              color: 'rgba(16, 185, 129, 0.9)',
              fontWeight: '500'
            }}>
              {currentSelectedSchools.length} school{currentSelectedSchools.length !== 1 ? 's' : ''} selected
            </span>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {currentSelectedSchools.length > 3 && (
                <button
                  onClick={toggleShowAllSelected}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'rgba(16, 185, 129, 0.8)',
                    fontSize: '11px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(16, 185, 129, 0.1)';
                    e.target.style.color = 'rgba(16, 185, 129, 1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'none';
                    e.target.style.color = 'rgba(16, 185, 129, 0.8)';
                  }}
                >
                  {showAllSelected ? 'Show Less' : 'Show All'}
                </button>
              )}
              <button
                onClick={handleClearAll}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(239, 68, 68, 0.8)',
                  fontSize: '11px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(239, 68, 68, 0.1)';
                  e.target.style.color = 'rgba(239, 68, 68, 1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'none';
                  e.target.style.color = 'rgba(239, 68, 68, 0.8)';
                }}
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Selected Schools List with Enhanced Color Display */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            maxHeight: showAllSelected ? '200px' : 'auto',
            overflowY: showAllSelected && currentSelectedSchools.length > 6 ? 'auto' : 'visible'
          }}>
            {(showAllSelected ? currentSelectedSchools : currentSelectedSchools.slice(0, 3)).map(school => {
              const assignedColor = getSchoolAssignedColor(school);
              return (
                <div
                  key={school.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '6px',
                    padding: '6px 8px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    flex: 1,
                    minWidth: 0
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: assignedColor || getSchoolTypeColor(school.type),
                      border: assignedColor ? '1px solid rgba(255, 255, 255, 0.3)' : 'none',
                      flexShrink: 0
                    }}></div>
                    <div style={{ 
                      fontSize: '11px',
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontWeight: '500',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1
                    }}>
                      {school.name}
                    </div>
                    <div style={{
                      fontSize: '10px',
                      color: 'rgba(255, 255, 255, 0.5)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      padding: '1px 4px',
                      borderRadius: '3px',
                      flexShrink: 0
                    }}>
                      {getSchoolTypeLabel(school.type)}
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleRemoveSchool(school, e)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'rgba(239, 68, 68, 0.6)',
                      cursor: 'pointer',
                      padding: '2px',
                      borderRadius: '3px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                      marginLeft: '8px',
                      flexShrink: 0
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(239, 68, 68, 0.1)';
                      e.target.style.color = 'rgba(239, 68, 68, 0.9)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'none';
                      e.target.style.color = 'rgba(239, 68, 68, 0.6)';
                    }}
                    title={`Remove ${school.name}`}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </div>
              );
            })}
            
            {!showAllSelected && currentSelectedSchools.length > 3 && (
              <div style={{
                fontSize: '10px',
                color: 'rgba(255, 255, 255, 0.6)',
                padding: '4px 8px',
                textAlign: 'center',
                fontStyle: 'italic'
              }}>
                and {currentSelectedSchools.length - 3} more school{currentSelectedSchools.length - 3 !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dropdown with Enhanced Color Display */}
      {isOpen && (
        <div style={{
          background: 'rgba(26, 32, 44, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          borderRadius: '8px',
          maxHeight: '300px',
          overflowY: 'auto',
          marginBottom: '16px'
        }}>
          {filteredSchools.length === 0 ? (
            <div style={{
              padding: '12px',
              textAlign: 'center',
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: '12px'
            }}>
              No schools found
            </div>
          ) : (
            filteredSchools.map(school => {
              const isSelected = currentSelectedSchools.some(s => s.id === school.id);
              const assignedColor = getSchoolAssignedColor(school);
              
              return (
                <div
                  key={school.id}
                  onClick={() => handleSchoolToggle(school)}
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    transition: 'all 0.2s ease',
                    background: isSelected ? 'rgba(16, 185, 129, 0.1)' : 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = isSelected ? 'rgba(16, 185, 129, 0.1)' : 'transparent';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '500',
                        color: 'rgba(255, 255, 255, 0.9)',
                        marginBottom: '2px',
                        lineHeight: '1.3'
                      }}>
                        {school.name}
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: assignedColor || getSchoolTypeColor(school.type),
                            border: assignedColor ? '1px solid rgba(255, 255, 255, 0.3)' : 'none'
                          }}></div>
                          <span style={{
                            fontSize: '11px',
                            color: 'rgba(255, 255, 255, 0.6)',
                            fontWeight: '400'
                          }}>
                            {getSchoolTypeLabel(school.type)}
                          </span>
                        </div>
                        {school.cluster !== 'N/A' && (
                          <>
                            <span style={{
                              fontSize: '11px',
                              color: 'rgba(255, 255, 255, 0.4)'
                            }}>
                              •
                            </span>
                            <span style={{
                              fontSize: '11px',
                              color: 'rgba(255, 255, 255, 0.5)',
                              maxWidth: '100px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {school.cluster}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '3px',
                      background: isSelected ? '#10B981' : 'rgba(255, 255, 255, 0.1)',
                      border: '2px solid ' + (isSelected ? '#10B981' : 'rgba(255, 255, 255, 0.3)'),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginLeft: '12px',
                      flexShrink: 0
                    }}>
                      {isSelected && (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="white">
                          <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Stats */}
      <div style={{
        fontSize: '11px',
        color: 'rgba(255, 255, 255, 0.5)',
        textAlign: 'center',
        marginTop: '8px'
      }}>
        {allSchools.length} total schools in {currentYear}
      </div>
    </div>
  );
};

export default SchoolFilter;

// Export normalization functions for use in MapContainer
export const normalizeSchoolName = (name, type) => {
  if (!name || typeof name !== 'string') return '';
  
  let normalized = name.trim()
    .replace(/\s+/g, ' ')
    .replace(/\s*(Elementary|ES|E\.S\.)(\s*School)?$/i, '')
    .replace(/\s*(Middle|MS|M\.S\.)(\s*School)?$/i, '')
    .replace(/\s*(High|HS|H\.S\.)(\s*School)?$/i, '')
    .replace(/\s*School$/i, '')
    .trim();

  const specialCases = {
    'B-CC': 'Bethesda-Chevy Chase',
    'BCC': 'Bethesda-Chevy Chase',
    'Bethesda Chevy Chase': 'Bethesda-Chevy Chase',
    'RMS': 'Richard Montgomery',
    'RM': 'Richard Montgomery',
    'WJ': 'Walter Johnson',
    'Blair': 'Montgomery Blair',
    'Einstein': 'Albert Einstein',
    'Wootton': 'Thomas S. Wootton',
    'Churchill': 'Winston Churchill',
    'Whitman': 'Walt Whitman'
  };

  if (specialCases[normalized]) {
    normalized = specialCases[normalized];
  }

  return normalized;
};

export const createSchoolKey = (name, type) => {
  const normalized = normalizeSchoolName(name, type);
  return `${type}:${normalized}`;
};